import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { NavLink } from 'react-router-dom';
import { classNames } from '../../../utils/helpers/helpers';
import { isHrefActive } from './navigation';

// flex-shrink-0 + whitespace-nowrap — несущие классы: без них flex-ряд сжимает
// вкладки и переносит подписи внутри себя (это и был исходный баг навбара).
export const TAB_BASE =
  'flex-shrink-0 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium';
export const TAB_ACTIVE = 'bg-gray-900 text-white';
export const TAB_IDLE = 'text-gray-300 hover:bg-gray-700 hover:text-white';

const MENU_TRANSITION = {
  enter: 'transition ease-out duration-100',
  enterFrom: 'transform opacity-0 scale-95',
  enterTo: 'transform opacity-100 scale-100',
  leave: 'transition ease-in duration-75',
  leaveFrom: 'transform opacity-100 scale-100',
  leaveTo: 'transform opacity-0 scale-95',
};

/** Плоская вкладка верхнего уровня (desktop). */
export function NavTab({ item, pathname }) {
  const active = isHrefActive(pathname, item.href);

  return (
    <NavLink
      to={item.href}
      className={classNames(TAB_BASE, active ? TAB_ACTIVE : TAB_IDLE)}
    >
      <span className='relative flex h-full flex-col justify-between'>
        {item.isNew && (
          <span className='absolute -top-3 -right-4 z-10 rounded-md border border-red-600 bg-red-600 px-1 text-xs font-bold text-white'>
            new
          </span>
        )}
        <span>{item.shortName || item.name}</span>
        <span
          data-active={active}
          className='invisible mt-1 block border-b-2 border-purple-600 data-[active=true]:visible'
        />
      </span>
    </NavLink>
  );
}

/** Группа-выпадашка (desktop). Копирует структуру пользовательского меню в NavBar. */
export function NavDropdown({ group, pathname }) {
  const active = group.children.some((child) =>
    isHrefActive(pathname, child.href),
  );

  return (
    <Menu as='div' className='relative flex flex-shrink-0'>
      {({ open }) => (
        <>
          <Menu.Button
            className={classNames(
              TAB_BASE,
              active || open ? TAB_ACTIVE : TAB_IDLE,
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-gray-800',
            )}
          >
            {/* только span-ы: <button> допускает phrasing content, не <div> */}
            <span className='relative flex h-full flex-col justify-between'>
              {group.hasNew && (
                <span
                  className='absolute -top-1 -right-2 z-10 h-2 w-2 rounded-full bg-red-600'
                  aria-hidden='true'
                />
              )}
              <span className='flex items-center gap-1'>
                {group.name}
                {/* ВАЖНО: НЕ ui-open:. Плагин @headlessui/tailwindcss
                    регистрирует ui-open в том числе как ПОТОМКОВЫЙ селектор
                    :where([data-headlessui-state~="open"]) &, а корневой
                    <Disclosure as='nav'> сам получает этот атрибут при
                    открытой мобильной панели -> шевроны залипали бы. */}
                <ChevronDownIcon
                  className={classNames(
                    'h-4 w-4 transition-transform',
                    open ? 'rotate-180' : '',
                  )}
                  aria-hidden='true'
                />
              </span>
              <span
                data-active={active}
                className='invisible mt-1 block border-b-2 border-purple-600 data-[active=true]:visible'
              />
            </span>
          </Menu.Button>

          <Transition as={Fragment} {...MENU_TRANSITION}>
            {/* z-30: самый высокий sticky в проекте — z-20 */}
            <Menu.Items className='ring-black absolute left-0 top-full z-30 mt-1 w-max min-w-[14rem] max-w-[22rem] origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-opacity-5 focus:outline-none'>
              {group.children.map((child) => {
                const childActive = isHrefActive(pathname, child.href);
                return (
                  <Menu.Item key={child.id}>
                    {({ active: focused }) => (
                      <NavLink
                        to={child.href}
                        className={classNames(
                          focused ? 'bg-gray-100' : '',
                          childActive
                            ? 'bg-gray-50 font-semibold text-gray-900'
                            : 'text-gray-700',
                          'flex items-center justify-between gap-3 whitespace-nowrap px-4 py-2 text-sm',
                        )}
                      >
                        <span>{child.name}</span>
                        {child.isNew && (
                          <span className='rounded-md bg-red-600 px-1 text-xs font-bold text-white'>
                            new
                          </span>
                        )}
                      </NavLink>
                    )}
                  </Menu.Item>
                );
              })}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
}

/** Ссылка в мобильной панели. */
export function MobileNavLink({ item, pathname, onNavigate }) {
  const active = isHrefActive(pathname, item.href);

  return (
    <NavLink
      to={item.href}
      onClick={onNavigate}
      className={classNames(
        active
          ? 'bg-gray-900 text-white'
          : 'text-gray-300 hover:bg-gray-700 hover:text-white',
        'flex items-center justify-between rounded-md px-3 py-2 text-base font-medium',
      )}
    >
      <span>{item.name}</span>
      {item.isNew && (
        <span className='rounded-md bg-red-600 px-1 text-xs font-bold text-white'>
          new
        </span>
      )}
    </NavLink>
  );
}
